import { useMemo } from "react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Card } from "@/components/ui/card";
import { Shimmer, ChartShimmer } from "@/components/ui/shimmer";
import { useChartColors } from "@/hooks/useChartColors";

interface DailyPurchasesChartProps {
  transactions: Array<{ amount: number; created_at: string }>;
  loading: boolean;
}

export default function DailyPurchasesChart({ transactions, loading }: DailyPurchasesChartProps) {
  const colors = useChartColors();

  const buckets = useMemo(() => {
    const map = new Map<string, { totalAmount: number; count: number }>();

    for (const txn of transactions) {
      const date = txn.created_at.slice(0, 10);
      const entry = map.get(date) ?? { totalAmount: 0, count: 0 };
      entry.totalAmount += Math.abs(txn.amount);
      entry.count += 1;
      map.set(date, entry);
    }

    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, vals]) => {
        const d = new Date(date);
        const month = d.toLocaleString("en-US", { month: "short" });
        return {
          label: `${month} ${d.getDate()}`,
          totalAmount: +(vals.totalAmount / 100).toFixed(2),
          count: vals.count,
        };
      });
  }, [transactions]);

  return (
    <Shimmer loading={loading} fallback={<ChartShimmer height={400} />}>
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-base font-semibold">Daily Purchases Overview</h3>
          </div>

          {buckets.length === 0 ? (
          <div className="flex items-center justify-center h-[400px]">
            <span className="text-sm text-muted-foreground">No purchases in this period</span>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={buckets} margin={{ top: 5, bottom: 5, left: 0, right: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="amount" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="count" orientation="right" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{
                  borderRadius: "8px",
                  border: "1px solid var(--border)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  fontSize: "13px",
                }}
              />
              <Legend wrapperStyle={{ fontSize: "13px" }} />
              <Bar dataKey="totalAmount" name="Amount" yAxisId="amount" fill={colors.primary} radius={[4, 4, 0, 0]} />
              <Line dataKey="count" name="Purchases" yAxisId="count" stroke={colors.teal} strokeWidth={2} dot={{ r: 3, fill: colors.teal }} type="monotone" />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
      </Card>
    </Shimmer>
  );
}
